import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Pill } from '@/ui/primitives'
import { DataTable, type Coloana } from '@/ui/data-table'
import { NewMenu } from '@/ui/new-menu'
import { lei } from '@/lib/money'

type Partener = {
  id: number; name: string; type: string | null; supplier_customer: string | null
  city: string | null; phone: string | null; mobile: string | null; email: string | null
  vat_no: string | null; total_sales: number; total_purchases: number
  total_reservations: number; is_inactive: boolean
  trips?: number; birth_date?: string | null
  tags?: { nume: string; culoare: string }[]
}
/** Endpointul intoarce TOATI partenerii (6.536 la 31.08.2026) si ignora `limit` — verificat.
 *  De aceea lista se aduce o data, se tine in cache si se filtreaza local. */
type Lista = { partners: Partener[] }

export function Partners() {
  const navigheaza = useNavigate()
  const [rol, setRol] = useState<'toti' | 'clienti' | 'furnizori'>('toti')

  const { data, isPending } = useQuery({
    queryKey: ['parteneri'],
    queryFn: () => api.get<Lista>('/partners/'),
    staleTime: 5 * 60_000,
  })

  const rows = useMemo(() => {
    return (data?.partners ?? [])
      // Rolul e in `supplier_customer` (Customer 4.415, Supplier and Customer 1.946,
      // Supplier 239), NU in `type` — acela e forma juridica (Individual / Company).
      // Filtrat pe `type`, lista arata 95 de clienti din 6.536. Verificat in baza, 31.08.2026.
      .filter((p) => {
        const rp = p.supplier_customer ?? ''
        if (rol === 'clienti') return rp.includes('Customer')
        if (rol === 'furnizori') return rp.includes('Supplier')
        return true
      })
  }, [data, rol])

  const rolText = (p: Partener) =>
    p.supplier_customer === 'Supplier and Customer' ? 'client si furnizor'
      : (p.supplier_customer ?? '').includes('Supplier') ? 'furnizor'
      : (p.supplier_customer ?? '').includes('Customer') ? 'client' : '—'

  const coloane: Coloana<Partener>[] = [
    { cheie: 'name', antet: 'Nume', sortabil: true, valoare: (p) => p.name,
      randare: (p) => (
        <span>
          <span className="font-medium">{p.name}</span>
          {p.is_inactive && <span className="ml-2 text-[12px] text-muted">inactiv</span>}
          {p.vat_no && <span className="ml-2 text-[12px] text-muted tnum">{p.vat_no}</span>}
        </span>
      ) },
    { cheie: 'rol', antet: 'Rol', sortabil: true, valoare: (p) => rolText(p),
      randare: (p) => (
        <Badge tone={(p.supplier_customer ?? '').includes('Supplier') ? 'warn' : 'neutral'}>{rolText(p)}</Badge>
      ) },
    { cheie: 'forma', antet: 'Forma', sortabil: true, valoare: (p) => p.type ?? '',
      randare: (p) => (
        <span className="text-[13px] text-muted">
          {p.type === 'Individual' ? 'persoana fizica' : p.type === 'Company' ? 'companie' : (p.type ?? '—')}
        </span>
      ) },
    { cheie: 'contact', antet: 'Contact',
      randare: (p) => (
        <span className="block max-w-[220px] truncate text-[13px] text-muted">
          {[p.phone ?? p.mobile, p.email, p.city].filter(Boolean).join(' · ') || '—'}
        </span>
      ) },
    { cheie: 'nastere', antet: 'Nascut', sortabil: true, valoare: (p) => p.birth_date ?? '',
      randare: (p) => (p.birth_date
        ? <span className="tnum text-[13px] text-muted">
            {new Date(p.birth_date).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        : <span className="text-muted">—</span>) },
    { cheie: 'trips', antet: 'Calatorii', sortabil: true, aliniat: 'dreapta', valoare: (p) => p.trips ?? 0,
      randare: (p) => (p.trips ? <span className="tnum">{p.trips}</span> : <span className="text-muted">—</span>) },
    { cheie: 'etichete', antet: 'Etichete',
      randare: (p) => (p.tags?.length
        ? <span className="flex flex-wrap gap-1">
            {p.tags.map((t, i) => (
              <span key={i} className="inline-flex items-center h-6 px-2 rounded-full text-[11.5px]"
                style={{ backgroundColor: (t.culoare ?? '#888') + '22', color: t.culoare ?? undefined }}>
                {t.nume}
              </span>
            ))}
          </span>
        : <span className="text-muted">—</span>) },
    { cheie: 'vanzari', antet: 'Vanzari', sortabil: true, aliniat: 'dreapta', valoare: (p) => p.total_sales,
      randare: (p) => (p.total_sales ? <span className="tnum">{lei(p.total_sales)}</span> : <span className="text-muted">—</span>) },
    { cheie: 'cumparari', antet: 'Cumparari', sortabil: true, aliniat: 'dreapta', valoare: (p) => p.total_purchases,
      randare: (p) => (p.total_purchases ? <span className="tnum">{lei(p.total_purchases)}</span> : <span className="text-muted">—</span>) },
  ]

  return (
    <>
      <PageHeader title="Contacte"
        count={data ? `${rows.length.toLocaleString('ro-RO')} afisati` : undefined}
        actions={<NewMenu />} />
      <div className="px-8 py-6 space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          {(['toti', 'clienti', 'furnizori'] as const).map((r) => (
            <Pill key={r} active={rol === r} onClick={() => setRol(r)}>
              {r === 'toti' ? 'Toti' : r === 'clienti' ? 'Clienti' : 'Furnizori'}
            </Pill>
          ))}
        </div>

        <DataTable
          randuri={rows} coloane={coloane} cheie={(p) => p.id} incarca={isPending}
          peRand={(p) => navigheaza(`/contacte/${p.id}`)}
          sortImplicit={{ cheie: 'vanzari', sens: 'desc' }}
          cauta={(p, t) => `${p.name} ${p.vat_no ?? ''} ${p.city ?? ''} ${p.email ?? ''}`.toLowerCase().includes(t)}
          placeholderCautare="Nume, CUI, oras…"
          gol={{ titlu: 'Niciun partener', indiciu: 'Incearca alt termen de cautare.' }}
        />
      </div>
    </>
  )
}
